import { BigNumber } from "@yjsgames/bignumber";
import { DUNGEON_BALANCE, REWARD_BUNDLES } from "../data/rewardData.js";
import type { RewardSpec } from "../models/RewardSpec.js";

export interface DungeonState {
  currentIndex: number;
  clearedMaxIndex: number;
  ticketsRemaining: number;
}

/**
 * 던전 진행 — 순수 로직 (Phaser 비의존, 단위 테스트 대상).
 * 무한 인덱스(현재 도전 currentIndex / 클리어 최고 clearedMaxIndex), 일일 도전권,
 * 보상·벽돌HP 공식, 소탕 조건을 담당한다. 실제 전투/지급은 GameScene이 수행.
 * 일일 자정 리셋(도전권 충전 트리거)은 @yjsgames/daily-reset가 구동 → GameScene이 refill() 호출.
 * 인덱스는 0-based — UI 표시 시 +1. clearedMaxIndex=-1 은 클리어한 던전 없음.
 */
export class DungeonController {
  private _currentIndex: number;
  private _clearedMaxIndex: number;
  private _tickets: number;
  private readonly limit: number;

  constructor(initial?: Partial<DungeonState>, limit = DUNGEON_BALANCE.dailyChallengeLimit) {
    this.limit = Math.max(0, Math.floor(limit));
    this._currentIndex = Math.max(0, Math.floor(initial?.currentIndex ?? 0));
    this._clearedMaxIndex = Math.floor(initial?.clearedMaxIndex ?? -1);
    this._tickets = Math.max(0, Math.floor(initial?.ticketsRemaining ?? this.limit));
  }

  get currentIndex(): number {
    return this._currentIndex;
  }
  get clearedMaxIndex(): number {
    return this._clearedMaxIndex;
  }
  get ticketsRemaining(): number {
    return this._tickets;
  }
  get ticketLimit(): number {
    return this.limit;
  }

  /** 도전권을 한도로 충전 (일일 자정 리셋 시 @yjsgames/daily-reset 콜백에서 호출). */
  refill(): void {
    this._tickets = this.limit;
  }

  canChallenge(): boolean {
    return this._tickets > 0;
  }

  /** 도전권 1 소모. 부족 시 무변경 + false. */
  consumeTicket(): boolean {
    if (this._tickets <= 0) return false;
    this._tickets -= 1;
    return true;
  }

  /** 현재 던전 클리어 처리 — clearedMax 갱신 + currentIndex +1. (도전 전투 승리 시 호출) */
  markCleared(): void {
    if (this._currentIndex > this._clearedMaxIndex) this._clearedMaxIndex = this._currentIndex;
    this._currentIndex += 1;
  }

  /** 소탕 가능 — 클리어한 던전 1개 이상 + 도전권 보유. */
  canSweep(): boolean {
    return this._clearedMaxIndex >= 0 && this._tickets > 0;
  }

  /** 소탕 — 도전권 1 소모(인덱스 변화 없음). 보상은 clearedMaxIndex 던전. 가드 실패 시 false. */
  sweep(): boolean {
    if (!this.canSweep()) return false;
    return this.consumeTicket();
  }

  /** 치트/보상용 — 도전권 직접 설정. */
  setTickets(n: number): void {
    this._tickets = Math.max(0, Math.floor(n));
  }

  /** 던전 index 보상 — 종류=주기 번들(index % cyclePeriod), 수량=인덱스 비례 공식으로 덮어씀. */
  static rewardForIndex(index: number): RewardSpec[] {
    const i = Math.max(0, Math.floor(index));
    const cycleId = `dungeonCycle${i % DUNGEON_BALANCE.cyclePeriod}`;
    const base = REWARD_BUNDLES[cycleId] ?? [];
    return base.map((spec) => DungeonController.scaleSpec(spec, i));
  }

  /** gold/diamond/upgradeStone 수량을 base×growth^index로 덮어씀. randomModule/rebirthPoint/artifact는 그대로. */
  private static scaleSpec(spec: RewardSpec, index: number): RewardSpec {
    switch (spec.kind) {
      case "gold":
        return {
          kind: "gold",
          amount: BigNumber.fromNumber(DUNGEON_BALANCE.goldBase)
            .mul(BigNumber.fromNumber(DUNGEON_BALANCE.goldGrowth).pow(index))
            .toString(),
        };
      case "diamond":
        return {
          kind: "diamond",
          amount: BigNumber.fromNumber(DUNGEON_BALANCE.diamondBase)
            .mul(BigNumber.fromNumber(DUNGEON_BALANCE.diamondGrowth).pow(index))
            .toString(),
        };
      case "upgradeStone":
        return {
          kind: "upgradeStone",
          amount: Math.max(
            1,
            Math.round(DUNGEON_BALANCE.stoneBase * Math.pow(DUNGEON_BALANCE.stoneGrowth, index)),
          ),
        };
      default:
        return spec;
    }
  }

  /** 던전 index 벽돌 HP = base × growth^index (BigNumber, 무한 성장). */
  static brickHp(index: number): BigNumber {
    return BigNumber.fromNumber(DUNGEON_BALANCE.brickBaseHp).mul(
      BigNumber.fromNumber(DUNGEON_BALANCE.brickHpGrowth).pow(Math.max(0, Math.floor(index))),
    );
  }

  toState(): DungeonState {
    return {
      currentIndex: this._currentIndex,
      clearedMaxIndex: this._clearedMaxIndex,
      ticketsRemaining: this._tickets,
    };
  }
}
